import React, { Fragment, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import BudgetsTable from "../../components/BudgetsTable.jsx";
import TableCard from "../../components/ui-components/TableCard";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot.jsx";
import FilterDropdown from "../../components/ui-components/FilterDropdown";
import { primaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import EmptyState from "../../components/ui-components/EmptyState";
import Modal from "../../components/ui-components/Modal";
import BudgetDetailDrawer from "../cost-control/BudgetDetailDrawer";
import { useFinancialControlSummary, currentPeriod } from "../cost-control/useFinancialControlSummary";
import { listBudgets, createBudget, archiveBudget, getBudgetForecastTotals } from "./api";
import { forecastsByBudgetId } from "./utils/budgetForecastColumn";
import {
  formatAmountPrimary,
  formatAmountSecondary,
  formatPeriodLabel,
  formatScopeSummary,
  formatRelativeTime,
} from "./formatters";
import { toast } from '../../components/ui-components/Toast';

// Build a lowercase haystack from every column the table renders, so the search box
// matches across all columns (name, scope, amount, period, status, currency, updated).
function budgetHaystack(b) {
  const periodCount = b.periods?.length || null;
  return [
    b.name,
    formatScopeSummary(b.scope_summary),
    formatAmountPrimary(b.total_amount_cents, b.period_type, b.currency, periodCount),
    formatAmountSecondary(b.total_amount_cents, b.period_type, b.currency),
    formatPeriodLabel(b.period_type),
    b.status,
    b.currency,
    formatRelativeTime(b.updated_at),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

// distinctOptions — a FilterDropdown option list ({value,label}) of the distinct,
// non-empty values a getter yields across the rows, prefixed with an "All" option.
function distinctOptions(rows, get, labelFn = (v) => v, allLabel = "All") {
  const seen = new Map();
  for (const r of rows) {
    const v = get(r);
    if (v == null || v === "") continue;
    if (!seen.has(v)) seen.set(v, labelFn(v));
  }
  return [{ value: "all", label: allLabel }, ...[...seen].map(([value, label]) => ({ value: String(value), label }))];
}

const BudgetsListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Status is a row-2 tab, driven by the ?status= query param (see headerTabs.js);
  // "all" (no param) is the default and returns every status.
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "all";
  const [searchQuery, setSearchQuery] = useState("");

  // Modals state
  const [archiveModal, setArchiveModal] = useState({ open: false, budget: null });
  const [duplicateModal, setDuplicateModal] = useState({ open: false, budget: null });

  // Fetch budgets for the active status filter; search is applied client-side so it can
  // match across every column (not just the name the backend's `q` would search).
  const { data, isLoading, error } = useQuery(
    ["budgets", statusFilter],
    () => listBudgets({ status: statusFilter === "all" ? undefined : statusFilter }),
    { keepPreviousData: true }
  );

  // Projections for the Forecast column. Independent of the budgets query and NOT gating
  // it: a failure here must leave the table rendering with "-" in that one column rather
  // than taking the page down, so no error is surfaced from it.
  const { data: forecastData } = useQuery(
    ["budget-forecast-monthly"],
    getBudgetForecastTotals,
    { retry: false }
  );
  const forecastsById = useMemo(() => forecastsByBudgetId(forecastData), [forecastData]);

  // Preview drawer. It's the same BudgetDetailDrawer the /financial-control dashboard
  // opens, so a budget reads identically from either screen; its figures come from the
  // control summary's budget_risk rows (shared react-query cache with that page).
  const [selected, setSelected] = useState(null);
  const period = currentPeriod();
  const { data: controlSummary } = useFinancialControlSummary(period);
  const riskById = useMemo(() => {
    const m = new Map();
    for (const b of controlSummary?.kpis?.budget_risk?.budgets || []) m.set(String(b.id), b);
    return m;
  }, [controlSummary]);

  const budgets = useMemo(() => data?.budgets || [], [data]);
  // The "All" tab excludes archived budgets — they have their own tab. Everything
  // downstream (filter options + rows) works off this scoped set.
  const scopedBudgets = useMemo(
    () => (statusFilter === "all" ? budgets.filter((b) => b.status !== "archived") : budgets),
    [budgets, statusFilter]
  );

  // Secondary filters (client-side) — owner (scope), type (kind) and period. Options
  // are derived from the loaded rows so they only offer values that actually exist.
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");
  const ownerOptions = useMemo(() => distinctOptions(scopedBudgets, (b) => b.scope_summary?.text, (v) => v, "All owners"), [scopedBudgets]);
  const typeOptions = useMemo(() => distinctOptions(scopedBudgets, (b) => b.kind, (v) => v, "All types"), [scopedBudgets]);
  const periodOptions = useMemo(() => distinctOptions(scopedBudgets, (b) => b.period_type, formatPeriodLabel, "All periods"), [scopedBudgets]);

  const search = searchQuery.trim();
  const searchLower = search.toLowerCase();
  const filteredBudgets = useMemo(
    () => scopedBudgets.filter((b) => {
      if (searchLower && !budgetHaystack(b).includes(searchLower)) return false;
      if (ownerFilter !== "all" && (b.scope_summary?.text || "") !== ownerFilter) return false;
      if (typeFilter !== "all" && String(b.kind || "") !== typeFilter) return false;
      if (periodFilter !== "all" && String(b.period_type || "") !== periodFilter) return false;
      return true;
    }),
    [scopedBudgets, searchLower, ownerFilter, typeFilter, periodFilter]
  );

  // Shown-count hint (relocated to row 2). Reflects the live match count + search term.
  const matchCount = filteredBudgets.length;
  const noun = matchCount === 1 ? "budget" : "budgets";
  // "all" reads as just "budgets"; a specific status qualifies the noun.
  const statusWord = statusFilter === "all" ? "" : `${statusFilter} `;
  const hint = isLoading
    ? "Loading…"
    : search
      ? `${matchCount} ${statusWord}${noun} matching "${search}"`
      : `${matchCount} ${statusWord}${noun}`;

  // Row click previews the budget in the drawer; the row's Details button goes straight
  // to its page. The drawer's `selected` is the control summary's risk row (cap / actual /
  // projected / days-to-breach); a budget with no risk row — a draft, or one the engine
  // can't measure — still opens, and the drawer falls back to its own series for figures.
  const openPreview = (id) => {
    const row = scopedBudgets.find((b) => String(b.id) === String(id));
    const risk = riskById.get(String(id));
    setSelected({ ...(risk || {}), id: Number(id), name: row?.name || risk?.name || "Budget" });
  };

  // Mutations
  const archiveMutation = useMutation(
    ({ id }) => archiveBudget(id),
    {
      onSuccess: () => {
        toast.success("Budget archived");
        queryClient.invalidateQueries(["budgets"]);
        setArchiveModal({ open: false, budget: null });
      },
      onError: (err) => {
        toast.error("Failed to archive budget");
        console.error(err);
      }
    }
  );

  const duplicateMutation = useMutation(
    (payload) => createBudget(payload),
    {
      onSuccess: (data) => {
        toast.success("Budget duplicated");
        queryClient.invalidateQueries(["budgets"]);
        setDuplicateModal({ open: false, budget: null });
        navigate(`/budgets/${data.id}`);
      },
      onError: (err) => {
        toast.error("Failed to duplicate budget");
        console.error(err);
      }
    }
  );


  return (
    <Fragment>
      <AdministrationHeader
        title="Financial Limits"
        className="text-text-3"
        showPeriodSelector={false}
      />
      {/* New budget is the page's primary action, in the row-1 header cluster. */}
      <HeaderPortal slot="actions">
        <button type="button" onClick={() => navigate("/budgets/new")} className={primaryButtonCls}>
          <FontAwesomeIcon icon={icon({ name: "plus", style: "solid" })} className="mr-1.5 text-[12px]" />
          New budget
        </button>
      </HeaderPortal>
      <div className="w-full px-6 py-6">
        {/* Search + secondary filters (owner / type / period) sit on the card's first
            row; the shown count sits below them on the second row. */}
        <TableCard
          subtitle={hint}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search budgets…"
          controls={
            <>
              {ownerOptions.length > 1 && (
                <FilterDropdown ariaLabel="Filter by owner" prefix="Owner:" value={ownerFilter} onChange={setOwnerFilter} options={ownerOptions} minWidth={130} height={36} />
              )}
              {typeOptions.length > 1 && (
                <FilterDropdown ariaLabel="Filter by type" prefix="Type:" value={typeFilter} onChange={setTypeFilter} options={typeOptions} minWidth={120} height={36} />
              )}
              {periodOptions.length > 1 && (
                <FilterDropdown ariaLabel="Filter by period" prefix="Period:" value={periodFilter} onChange={setPeriodFilter} options={periodOptions} minWidth={120} height={36} />
              )}
            </>
          }
          error={error}
          errorMessage="Error loading budgets"
          onRetry={() => queryClient.invalidateQueries(["budgets"])}
        >
          {/* No spinner: the fetch keeps the previous rows (keepPreviousData) and the
              subtitle already says "Loading…", so swapping the table for a spinner only
              made the page flash. A first load simply shows nothing until rows arrive —
              never the "No budgets yet" empty state, which would be a false claim. */}
          {isLoading && scopedBudgets.length === 0 ? null : scopedBudgets.length === 0 ? (
            statusFilter === "archived" ? (
              <EmptyState
                icon={icon({ name: "box-archive", style: "light" })}
                title="No archived budgets"
                message="Budgets you archive will appear here."
              />
            ) : (
              <EmptyState
                icon={icon({ name: "wallet", style: "light" })}
                title="No budgets yet"
                message="Create a budget to set financial limits for a scope."
                action={{
                  label: "Create budget",
                  onClick: () => navigate("/budgets/new"),
                  icon: icon({ name: "plus", style: "regular" }),
                }}
              />
            )
          ) : filteredBudgets.length === 0 ? (
            <EmptyState
              icon={icon({ name: "magnifying-glass" })}
              title="No matching budgets"
              message={`No ${statusWord}budgets match "${search}".`}
              action={{
                label: "Clear search",
                onClick: () => setSearchQuery(""),
                icon: icon({ name: "xmark", style: "regular" }),
                variant: "secondary",
              }}
            />
          ) : (
            <BudgetsTable
              data={filteredBudgets}
              forecastsById={forecastsById}
              onRowClick={openPreview}
              onOpenDetails={(id) => navigate(`/budgets/${id}`)}
            />
          )}
        </TableCard>
      </div>

      {/* Same drawer as /financial-control. Its CTA says "Open details" here: the row is
          already in front of you, so the next step is the budget's own page. */}
      <BudgetDetailDrawer
        selected={selected}
        period={period}
        onClose={() => setSelected(null)}
        ctaLabel="Open details"
      />

      {/* Archive Modal */}
      <Modal
        show={archiveModal.open}
        close={() => setArchiveModal({ open: false, budget: null })}
        title="Archive budget?"
        description="This budget will stop applying to future spend. Past data is preserved."
        confirmButton="Archive"
        confirmAction={() => archiveMutation.mutate({ id: archiveModal.budget.id })}
        icon={icon({ name: "box-archive", style: "solid" })}
      />

      {/* Duplicate Modal */}
      <Modal
        show={duplicateModal.open}
        close={() => setDuplicateModal({ open: false, budget: null })}
        title="Duplicate Budget"
        description="Create a copy of this budget. You can modify details after."
        confirmButton="Create Copy"
        confirmAction={() => {
           const payload = {
              name: `Copy of ${duplicateModal.budget.name}`,
              budget_type: duplicateModal.budget.budget_type,
              period_type: duplicateModal.budget.period_type,
              currency: duplicateModal.budget.currency,
              description: duplicateModal.budget.description
           };
           duplicateMutation.mutate(payload);
        }}
        icon={icon({ name: "copy", style: "solid" })}
      />
    </Fragment>
  );
};

export default BudgetsListPage;
